import { store, useAppStore } from '../store/appStore';
import { loadMeshFile } from '../import/loaders';
import { exportProject3mf, shareOrDownload3mf } from '../export/project3mf';
import { capturePlateThumbnail } from '../scene/PlateViewer';
import { estimatePrint } from '../export/estimate';
import type { ToolMode } from '../store/types';
import { useRef, useState } from 'react';

const TOOLS: { id: ToolMode; label: string }[] = [
  { id: 'select', label: 'Select' },
  { id: 'move', label: 'Move' },
  { id: 'rotate', label: 'Rotate' },
  { id: 'scale', label: 'Scale' },
  { id: 'paint-enforce', label: 'Support+' },
  { id: 'paint-block', label: 'Block' },
  { id: 'paint-erase', label: 'Erase' },
];

export function Toolbar() {
  const fileRef = useRef<HTMLInputElement>(null);
  const tool = useAppStore((s) => s.tool);
  const brushSize = useAppStore((s) => s.brushSize);
  const overhangHighlight = useAppStore((s) => s.overhangHighlight);
  const selectedId = useAppStore((s) => s.selectedId);
  const objects = useAppStore((s) => s.objects);
  const settings = useAppStore((s) => s.settings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const est = estimatePrint(objects, settings);

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      try {
        const mesh = await loadMeshFile(f);
        store.addMesh(mesh.name, mesh.positions);
        setMsg(`Loaded ${mesh.name}`);
      } catch (e) {
        setMsg(`Failed: ${(e as Error).message}`);
      }
    }
  }

  async function onExport() {
    if (!objects.length) {
      setMsg('Add a model first');
      return;
    }
    setBusy(true);
    setMsg('Building project 3MF…');
    try {
      const tpl = await fetch('/bambu/project_settings.config').then((r) => r.text());
      const thumb = await capturePlateThumbnail();
      const data = exportProject3mf({
        projectSettingsText: tpl,
        settings: store.getSnapshot().settings,
        objects: store.getSnapshot().objects,
        projectName: store.getSnapshot().projectName,
        thumbnailPng: thumb,
      });
      const name = `${store.getSnapshot().projectName || 'project'}.3mf`;
      const how = await shareOrDownload3mf(data, name);
      setMsg(how === 'shared' ? 'Shared via system sheet' : `Downloaded ${name}`);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setMsg(`Export failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="toolbar" data-testid="toolbar">
      <div className="brand">
        <strong>P1S Slicer</strong>
        <span className="sub">Bambu project · client-side</span>
      </div>

      <button type="button" className="primary" onClick={() => fileRef.current?.click()}>
        Upload
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".stl,.obj,.3mf,model/stl,model/obj,model/3mf"
        multiple
        hidden
        onChange={(e) => onFiles(e.target.files)}
      />

      <div className="tool-group">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tool === t.id ? 'active' : ''}
            onClick={() => store.setTool(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tool.startsWith('paint') && (
        <label className="brush">
          Brush
          <input
            type="range"
            min={1}
            max={20}
            step={0.5}
            value={brushSize}
            onChange={(e) => store.set({ brushSize: parseFloat(e.target.value) })}
          />
          <span>{brushSize}mm</span>
        </label>
      )}

      <button
        type="button"
        className={overhangHighlight ? 'active' : ''}
        onClick={() => store.set({ overhangHighlight: !overhangHighlight })}
      >
        Overhangs
      </button>

      <div className="obj-actions">
        <button type="button" disabled={!selectedId} onClick={() => store.centreSelected()}>
          Centre
        </button>
        <button type="button" disabled={!selectedId} onClick={() => store.dropSelectedToBed()}>
          Drop
        </button>
        <button type="button" disabled={!objects.length} onClick={() => store.autoArrange()}>
          Arrange
        </button>
        <button type="button" disabled={!selectedId} onClick={() => store.duplicateSelected()}>
          Dup
        </button>
        <button type="button" disabled={!selectedId} onClick={() => store.deleteSelected()}>
          Del
        </button>
        <button
          type="button"
          disabled={!selectedId}
          onClick={() => {
            const o = store.getSnapshot().objects.find((x) => x.id === selectedId);
            if (!o) return;
            // Lay flat: rotate so largest face down — simple: set rot to 0
            store.updateObject(o.id, { rotation: [0, 0, 0] });
            store.dropSelectedToBed();
          }}
          title="Reset orientation and drop to bed"
        >
          Lay flat
        </button>
      </div>

      <div className="estimate" title={est.note}>
        <span>~{est.filamentGrams}g</span>
        <span>~{est.approxMinutes} min</span>
        <em>estimate</em>
      </div>

      <button type="button" className="primary export" disabled={busy} onClick={onExport}>
        {busy ? 'Exporting…' : 'Export .3mf'}
      </button>

      <button
        type="button"
        className="mobile-only"
        onClick={() => store.set({ settingsOpen: !store.getSnapshot().settingsOpen })}
      >
        Settings
      </button>

      {msg && <div className="toast">{msg}</div>}
    </div>
  );
}
